// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@sc/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    // focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    // minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    // bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    // partialOverlay = "partialOverlay",
    // partialOverlayPositioning = "partialOverlayPositioning",
    hideButton = "hideButton",
    // contentPadding = "contentPadding",
    // centerTextVertically = "centerTextVertically",
    // responsiveFontSizes = "responsiveFontSizes",
    // customFontSizes = "customFontSizes",
    // h1FontSize = "h1FontSize",
    // h2FontSize = "h2FontSize",
    // h3FontSize = "h3FontSize",
    // h4FontSize = "h4FontSize",
    // h5FontSize = "h5FontSize",
    // h6FontSize = "h6FontSize",
    // normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // Xng-Changes: SC-535
    imagePosition = "imagePosition",
    imageWidth = "imageWidth",
    imageAltText = "imageAltText",
    openInNewWindow = "openInNewWindow",
    content = "content",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        // [fields.focalPoint]:
        //     | "topLeft"
        //     | "topCenter"
        //     | "topRight"
        //     | "centerLeft"
        //     | "center"
        //     | "centerRight"
        //     | "bottomLeft"
        //     | "bottomCenter"
        //     | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        // [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        // [fields.partialOverlay]: boolean;
        // [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.hideButton]: boolean;
        // [fields.contentPadding]: number;
        // [fields.centerTextVertically]: boolean;
        // [fields.responsiveFontSizes]: boolean;
        // [fields.customFontSizes]: boolean;
        // [fields.h1FontSize]: number;
        // [fields.h2FontSize]: number;
        // [fields.h3FontSize]: number;
        // [fields.h4FontSize]: number;
        // [fields.h5FontSize]: number;
        // [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // Xng-Changes: SC-535
        [fields.imagePosition]: "right" | "left";
        [fields.imageWidth]: "half" | "third";
        [fields.imageAltText]: string;
        [fields.openInNewWindow]: boolean;
        [fields.content]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // XNG-Changes: SC-535
    heading?: TypographyProps;
    subHeading?: TypographyProps;
    content?: TypographyProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            // Xng-Changes: SC-535
            fields.openInNewWindow ? window.open(url) : (window.location.href = url);
        } else {
            // Xng-Changes: SC-535
            fields.openInNewWindow ? window.open(url) : history.push(url);
        }
    };

    // const backgroundStyles =
    //     fields.background === "image"
    //         ? `background-image: url(${parseImageUrl(fields.image)});
    //        background-size: cover;`
    //         : `background-color: ${fields.backgroundColor};`;

    // const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    // let minimumHeightStyles;
    // switch (fields.minimumHeight) {
    //     case "1/4 viewport":
    //         minimumHeightStyles = "min-height: 25vh;";
    //         break;
    //     case "1/2 viewport":
    //         minimumHeightStyles = "min-height: 50vh;";
    //         break;
    //     case "3/4 viewport":
    //         minimumHeightStyles = "min-height: 75vh;";
    //         break;
    //     case "fullViewport":
    //         minimumHeightStyles = "min-height: 100vh;";
    //         break;
    // }

    // let overlayPositioningStyles;
    // if (!fields.partialOverlay) {
    //     overlayPositioningStyles = "align-items: stretch;";
    // } else if (fields.partialOverlayPositioning === "top") {
    //     overlayPositioningStyles = "align-items: flex-start;";
    // } else if (fields.partialOverlayPositioning === "middle") {
    //     overlayPositioningStyles = "align-items: center;";
    // } else if (fields.partialOverlayPositioning === "bottom") {
    //     overlayPositioningStyles = "align-items: flex-end;";
    // }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    // let fontSizeStyles;
    // if (fields.responsiveFontSizes || fields.customFontSizes) {
    //     fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    // }

    /* Xng-Changes: SC-535 */
    const customCssWrapper = {
        css: css`
            display: flex;
            flex-direction: ${fields.imagePosition === "left" ? "row" : "row-reverse"};
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    };

    /* Xng-Changes: SC-535 */
    const imageWrapperStyles = {
        css: css`
            background-color: ${fields.imageOverlay ? fields.imageOverlay : "transparent"};
            flex: 1;
        `,
    };

    /* Xng-Changes: SC-535 */
    const imageStyles = {
        css: css`
            ${xScreen("lte", "md")} {
                width: 100%;
            }
            img {
                width: 100%;
            }
        `,
    };

    /* Xng-Changes: SC-535 */
    const textWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            flex: ${fields.imageWidth === "half" ? 1 : 2};
            background-color: ${fields.backgroundColor ? fields.backgroundColor : "transparent"};
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* Xng-Changes: SC-535 - restructure */}
            <StyledWrapper className="banner-image-wrapper" {...imageWrapperStyles}>
                <LazyImage className="banner-image" {...imageStyles} src={fields.image} altText={fields.imageAltText} />
            </StyledWrapper>
            <StyledWrapper className="banner-text-wrapper" {...textWrapperStyles}>
                {fields.heading && (
                    <Typography className="banner-heading" {...styles.heading}>
                        {parse(fields.heading, parserOptions)}
                    </Typography>
                )}

                {fields.subheading && (
                    <Typography className="banner-subheading" {...styles.subHeading}>
                        {parse(fields.subheading, parserOptions)}
                    </Typography>
                )}
                {fields.content && (
                    <Typography className="banner-content" {...styles.content}>
                        {parse(fields.content, parserOptions)}
                    </Typography>
                )}

                {!fields.hideButton && (fields.buttonLabel || title || url) && (
                    <Button
                        {...styles.bannerButton}
                        variant={fields.variant}
                        onClick={onClick}
                        aria-label={fields.buttonLabel || title || url}
                    >
                        {fields.buttonLabel || title || url}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-image-wrapper {
}

.banner-image {

}

.banner-text-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            // {
            //     fieldType: "Contextual",
            //     name: fields.background,
            //     displayName: "Background",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Image", value: "image" },
            //         { displayName: "Color", value: "color" },
            //     ],
            //     defaultValue: "image",
            //     hideEmptyOption: true,
            //     tab: basicTab,
            // },
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                // isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // Xng-Changes: SC-535
            {
                fieldType: "Contextual",
                name: fields.imagePosition,
                displayName: "Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: basicTab,
            },
            // Xng-Changes: SC-535
            {
                fieldType: "Contextual",
                name: fields.imageWidth,
                displayName: "Image Width",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "50%", value: "half" },
                    { displayName: "33%", value: "third" },
                ],
                defaultValue: "third",
                hideEmptyOption: true,
                tab: basicTab,
            },
            // Xng-Changes: SC-535
            {
                fieldType: "Contextual",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Image Alt Text",
                defaultValue: "",
                tab: basicTab,
            },
            // Xng-Changes: SC-535
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                // isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // {
            //     fieldType: "Contextual",
            //     name: fields.partialOverlay,
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     tab: basicTab,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: fields.partialOverlayPositioning,
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Top", value: "top" },
            //         { displayName: "Middle", value: "middle" },
            //         { displayName: "Bottom", value: "bottom" },
            //     ],
            //     hideEmptyOption: true,
            //     defaultValue: "bottom",
            //     isVisible: widget => widget.fields.partialOverlay,
            //     tab: basicTab,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: fields.focalPoint,
            //     displayName: "Focal Point",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Top Left", value: "topLeft" },
            //         { displayName: "Top Center", value: "topCenter" },
            //         { displayName: "Top Right", value: "topRight" },
            //         { displayName: "Center Left", value: "centerLeft" },
            //         { displayName: "Center", value: "center" },
            //         { displayName: "Center Right", value: "centerRight" },
            //         { displayName: "Bottom Left", value: "bottomLeft" },
            //         { displayName: "Bottom Center", value: "bottomCenter" },
            //         { displayName: "Bottom Right", value: "bottomRight" },
            //     ],
            //     defaultValue: "center",
            //     hideEmptyOption: true,
            //     isVisible: widget => widget.fields.background === "image",
            //     tab: basicTab,
            // },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "Title",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "Subheading",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            // XNG-Change
            {
                fieldType: "Contextual",
                name: fields.content,
                editorTemplate: "RichTextField",
                displayName: "Content",
                defaultValue: "Content",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Label",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            // Xng-Changes: SC-535
            {
                fieldType: "Contextual",
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // {
            //     fieldType: "Contextual",
            //     name: fields.minimumHeight,
            //     displayName: "Minimum Banner Height",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "1/4 Viewport", value: "1/4 viewport" },
            //         { displayName: "1/2 Viewport", value: "1/2 viewport" },
            //         { displayName: "3/4 Viewport", value: "3/4 viewport" },
            //         { displayName: "Full Viewport", value: "fullViewport" },
            //     ],
            //     defaultValue: "1/4 viewport",
            //     hideEmptyOption: true,
            //     tab: basicTab,
            // },
            {
                fieldType: "Contextual",
                name: fields.hideButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // {
            //     name: fields.responsiveFontSizes,
            //     editorTemplate: "CheckboxField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     defaultValue: true,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: fields.contentPadding,
            //     editorTemplate: "IntegerField",
            //     defaultValue: 50,
            //     tab: basicTab,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: "centerTextVertically",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     tab: basicTab,
            // },
            // {
            //     name: fields.customFontSizes,
            //     editorTemplate: "CheckboxField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     defaultValue: false,
            // },
            // {
            //     name: fields.normalFontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: null,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h1FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: 40,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h2FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: 32,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h3FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: null,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h4FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: null,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h5FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: null,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            // {
            //     name: fields.h6FontSize,
            //     editorTemplate: "IntegerField",
            //     fieldType: "Contextual",
            //     tab: settingsTab,
            //     min: 1,
            //     defaultValue: null,
            //     isVisible: item => item?.fields[fields.customFontSizes],
            // },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
